import { useEffect, useState } from 'react';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { Icon } from '@/components/Icon';
import { SearchBox } from '@/components/form/SearchBox';
import { CustomSelect } from '@/components/form/CustomSelect';
import { SortableHeader } from '@/components/SortableHeader';
import { useColumnSort } from '@/lib/tableSort';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { showToast } from '@/lib/toast';
import {
  fetchGiveReport,
  fetchGiveReportFilters,
  downloadGiveReport,
  type GiveReportRow,
} from '@/api/reports';
import type { ClientsPageMeta } from '@/api/clients';

type SortKey = 'created_at' | 'recipient' | 'sender' | 'tutor' | 'operation' | 'value';

/**
 * "Выданные УЕ" — 1:1 старый Orchid ReportGiveListScreen: кто, кому и когда
 * выдавал у.е. Поиск идёт по получателю (в Orchid это был TD-фильтр колонки
 * "Получатель"), плюс фильтры по операции и куратору и выгрузка в Excel.
 *
 * Куратор без точного права 'platform.reports.give' видит только свои
 * операции — сужение делает сервер (см. докблок ReportsController).
 */
export function GiveReportTable() {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [operation, setOperation] = useState('');
  const [tutorId, setTutorId] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<GiveReportRow[] | null>(null);
  const [meta, setMeta] = useState<ClientsPageMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [operations, setOperations] = useState<string[]>([]);
  const [tutors, setTutors] = useState<{ value: string; label: string }[]>([]);
  const [exporting, setExporting] = useState(false);

  const sort = useColumnSort<SortKey>();
  const sortParam = sort.key ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;

  useEffect(() => {
    setPage(1);
  }, [search, operation, tutorId]);

  useEffect(() => {
    let cancelled = false;
    fetchGiveReportFilters()
      .then((filters) => {
        if (cancelled) return;
        setOperations(filters.operations);
        setTutors(filters.tutors);
      })
      .catch(() => {
        // Справочники не критичны: таблица работает и без выпадающих списков.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    fetchGiveReport({
      search: search || undefined,
      operation: operation || undefined,
      tutorId: tutorId || undefined,
      sort: sortParam,
      page,
    })
      .then(({ items, meta: pageMeta }) => {
        if (cancelled) return;
        setRows(items);
        setMeta(pageMeta);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить отчёт.');
      });
    return () => {
      cancelled = true;
    };
  }, [search, operation, tutorId, sortParam, page]);

  const exportReport = async () => {
    setExporting(true);
    try {
      await downloadGiveReport({
        search: search || undefined,
        operation: operation || undefined,
        tutorId: tutorId || undefined,
      });
    } catch {
      showToast('Не удалось выгрузить отчёт.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск по получателю" />
        <div className="group-select">
          <CustomSelect
            value={operation}
            options={[{ value: '', label: 'Все операции' }, ...operations.map((o) => ({ value: o, label: o }))]}
            onChange={setOperation}
          />
        </div>
        <div className="group-select">
          <CustomSelect
            value={tutorId}
            options={[{ value: '', label: 'Все кураторы' }, ...tutors]}
            onChange={setTutorId}
          />
        </div>
        <button
          type="button"
          className="btn btn--secondary filter-row__end"
          onClick={exportReport}
          disabled={exporting || rows === null}
        >
          <Icon name="externalLink" size={15} /> {exporting ? 'Готовим файл…' : 'Экспорт в Excel'}
        </button>
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="cell-muted">Операций не найдено.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <SortableHeader label="Дата" sortKey="created_at" sort={sort} onSort={sort.toggle} />
                <SortableHeader label="Получатель" sortKey="recipient" sort={sort} onSort={sort.toggle} />
                <SortableHeader label="Отправитель" sortKey="sender" sort={sort} onSort={sort.toggle} />
                <SortableHeader label="Куратор" sortKey="tutor" sort={sort} onSort={sort.toggle} />
                <SortableHeader label="Операция" sortKey="operation" sort={sort} onSort={sort.toggle} />
                <SortableHeader label="Сумма" sortKey="value" sort={sort} onSort={sort.toggle} className="cell-center" />
                <th>Описание</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td>{row.recipient ?? '—'}</td>
                  <td>{row.sender ?? '—'}</td>
                  <td>{row.tutor ?? '—'}</td>
                  <td>
                    <OperationCell operation={row.operation} />
                  </td>
                  <td className="cell-center">{row.value}</td>
                  <td className="cell-muted">{row.description ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta && meta.lastPage > 1 && (
        <div className="table-footer">
          <span className="table-footer__status">
            Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
          </span>
          <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
        </div>
      )}
    </>
  );
}

/**
 * Как показывается операция. Деньги приходят/уходят — зелёная вверх и
 * красная вниз; операции с резервом жёлтые, потому что баланс не меняется,
 * средства лишь блокируются и возвращаются:
 *  - Резервирование — «свободные ушли в резерв» (↓↑);
 *  - Разблокирование и Высвобождение — обратное, резерв вернулся (↑↓).
 * Незнакомые значения показываем текстом, без стрелки: направление наугад
 * подсказывать нельзя.
 */
const OPERATION_MARKS: Record<string, { tone: 'in' | 'out' | 'hold'; arrow: string }> = {
  Пополнение: { tone: 'in', arrow: '↑' },
  Списание: { tone: 'out', arrow: '↓' },
  Резервирование: { tone: 'hold', arrow: '↓↑' },
  Разблокирование: { tone: 'hold', arrow: '↑↓' },
  Высвобождение: { tone: 'hold', arrow: '↑↓' },
};

function OperationCell({ operation }: { operation: string | null }) {
  if (!operation) return <>—</>;

  const mark = OPERATION_MARKS[operation];
  if (!mark) return <>{operation}</>;

  return (
    <span className={`operation operation--${mark.tone}`}>
      {operation}
      <span className="operation__arrow" aria-hidden="true">
        {mark.arrow}
      </span>
    </span>
  );
}

/** Дата приходит уже в МСК (сервер переводит из UTC), поэтому просто разбираем строку. */
function formatDateTime(value: string): string {
  const [date, time] = value.replace('T', ' ').split(' ');
  if (!date) return '—';
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}${time ? ` ${time.slice(0, 8)}` : ''}`;
}
